


import React, { useState, useEffect } from 'react';
import Parse from 'parse';

import useUIPresentationMode from './useUIPresentationMode.js';

import '../styles/phcolours.css';


const EnquireModal = ({ isOpen, onClose }) => {

    const user = Parse.User.current();
    const [shareOption, setShareOption] = useState('someoneElse'); // 'someoneElse' | 'justMe'
    const [recipientEmail, setRecipientEmail] = useState('');
    const [yourEmail, setYourEmail] = useState('');
    const [canSend, setCanSend] = useState(false);

    const guestIdentifiers = ['guest@planhub.app'];
    const userIsGuest = user && guestIdentifiers.includes(user.get('username'));


    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);


    useEffect(() => {
        if (isOpen) {
            setShareOption('someoneElse');
            setRecipientEmail('');
            setYourEmail(user?.get('email') || '');
        }
    }, [isOpen, user]);


    useEffect(() => {

        const needsRecipient = shareOption === 'someoneElse';
        const needsYourEmail = userIsGuest;

        const validRecipient =
            !needsRecipient || /\S+@\S+\.\S+/.test(recipientEmail);
        const validYourEmail =
            !needsYourEmail || /\S+@\S+\.\S+/.test(yourEmail);

        setCanSend(validRecipient && validYourEmail);

    }, [recipientEmail, yourEmail, shareOption, userIsGuest]);


    const handleSend = async () => {

        const shareUrl = window.location.href;

        let toEmail = '';
        let senderEmail = '';
        let userFirstName = '';

        if (shareOption === 'justMe') {

            if (userIsGuest) {
                //console.log("User is guest - just me");
                toEmail = yourEmail;
            } else {
                toEmail = user.get('username');
                if (user) { userFirstName = user.get('firstName'); }
                //console.log("User is logged in - just me");
            }
        }
        else if (shareOption === 'someoneElse') {

            toEmail = recipientEmail;

            if (userIsGuest) {
                senderEmail = yourEmail;
                //console.log("Send to someone else and is guest.");
            } else {
                senderEmail = user.get('username');
                if (user) { userFirstName = user.get('firstName'); }
                //console.log("Send to someone else and is logged in user.");
            }
        }

        try {
            await navigator.clipboard.writeText(shareUrl);

            const sendToSelf = shareOption === 'justMe';

            const params = {
                toEmail,
                shareUrl,
                sendToSelf,
                userFirstName
            };

            if (senderEmail && senderEmail.includes('@')) {
                params.senderEmail = senderEmail;
            }

            /*
            console.log('SENDING EMAIL', {
                toEmail,
                senderEmail,
                sendToSelf,
                shareUrl,
                userFirstName
            });
            */

            await Parse.Cloud.run('sendShareEmailFromWebApp', params);

            //alert('Link copied and email sent!');
            onClose();
        } catch (err) {
            console.error(err);
            alert('Something went wrong sending the email.');
        }
    };


    // Scale this mofo depending on screen size.
    const { presentationModeSize } = useUIPresentationMode(); // Get the clean responsive size

    const [scaleFactor, setScaleFactor] = useState(0.9);

    useEffect(() => {
        if (presentationModeSize === 1 || presentationModeSize === 2) {  // Big!
            setScaleFactor(0.9);
        }
        if (presentationModeSize === 3) {  // Not as big.
            setScaleFactor(0.9);
        }
        if (presentationModeSize === 4 || presentationModeSize === 5) {  // Little!
            setScaleFactor(0.8);
        }
    }, [presentationModeSize]);


    if (!isOpen) return null;


    return (
        <div className="ui-share-modal" style={styles.overlay} onClick={onClose}>

            <div
                style={{
                    ...styles.modal,
                    transform: `translate(-50%, -50%) scale(${scaleFactor})`
                }}
                onClick={(e) => e.stopPropagation()}
            >

                <h2 style={styles.headerLabel}>Share this Plan</h2>

                <div style={{ marginTop: '10px' }}>
                    <label
                        style={styles.radioWrapper}
                        onMouseEnter={(e) => e.currentTarget.querySelector('span').style.color = 'var(--phblueaccent)'}
                        onMouseLeave={(e) => e.currentTarget.querySelector('span').style.color = 'var(--phblue)'}
                    >
                        <input
                            type="radio"
                            style={styles.radioInput}
                            value="someoneElse"
                            checked={shareOption === 'someoneElse'}
                            onChange={() => setShareOption('someoneElse')}
                        />
                        <span style={styles.radioLabel}>Send to someone else</span>
                    </label>
                    <label
                        style={styles.radioWrapper}
                        onMouseEnter={(e) => e.currentTarget.querySelector('span').style.color = 'var(--phblueaccent)'}
                        onMouseLeave={(e) => e.currentTarget.querySelector('span').style.color = 'var(--phblue)'}
                    >
                        <input
                            type="radio"
                            style={styles.radioInput}
                            value="justMe"
                            checked={shareOption === 'justMe'}
                            onChange={() => setShareOption('justMe')}
                        />
                        <span style={styles.radioLabel}>Just send to me</span>
                    </label>
                </div>

                <div style={{ marginTop: '25px' }}>
                    {/* Show recipient email if sending to someone else */}
                    {shareOption === 'someoneElse' && (
                        <>
                            <label style={styles.inputLabel}>Recipient email:</label>
                            <input
                                type="email"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                style={styles.inputField}
                            />
                        </>
                    )}

                    {/* Show your email if user is a guest or selected 'Just send to me' */}
                    {userIsGuest && (
                        <>
                            <label style={styles.inputLabel}>Your email:</label>
                            <input
                                type="email"
                                value={yourEmail}
                                onChange={(e) => setYourEmail(e.target.value)}
                                style={styles.inputField}
                            />
                        </>
                    )}

                </div>

                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={styles.cancel}>Cancel</button>
                    <button
                        onClick={handleSend}
                        disabled={!canSend}
                        style={{
                            ...styles.send,
                            backgroundColor: canSend ? 'var(--phgreenwashed)' : '#eee',
                            color: canSend ? 'var(--phgreenfont)' : 'var(--phgrey)',
                            cursor: canSend ? 'pointer' : 'not-allowed'
                        }}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};



const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        overflow: 'hidden',
        zIndex: '10001',
    },
    modal: {
        background: 'var(--phbluesuperwashedbackground)',
        borderRadius: '10px',
        padding: '30px',
        width: '400px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transformOrigin: 'center',
    },
    headerLabel: {
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--phblue)',
        padding: '0px 10px 10px 0px'
    },
    radioWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '13px',
        marginBottom: '13px',
        cursor: 'pointer'
    },
    radioInput: {
        accentColor: 'var(--phblue)',
        transform: 'scale(1.2)',
        cursor: 'pointer'
    },
    radioLabel: {
        fontSize: '15px',
        fontWeight: 500,
        color: 'var(--phblue)',
        transition: 'color 0.3s ease'
    },
    radioLabelHover: {
        color: 'var(--phblueaccent)'
    },
    inputLabel: {
        display: 'block',
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--phblue)',
        marginTop: '25px',
        marginBottom: '6px'
    },
    inputField: {
        width: '100%',
        padding: '8px 10px',
        fontSize: '14px',
        border: '1.5px solid var(--phblue)',
        borderRadius: '8px',
        backgroundColor: '#fff',
        color: 'var(--phblue)',
        outline: 'none',
    },
    cancel: {
        marginRight: '10px',
        padding: '10px 20px',
        backgroundColor: '#eee',
        border: 'none',
        borderRadius: '7px',
        cursor: 'pointer'
    },
    send: {
        padding: '10px 20px',
        border: 'none',
        borderRadius: '7px',
        backgroundColor: '#eee',
    }
};



export default EnquireModal;



